import * as React from "react"

import { alcanceFinanciero } from "@/lib/alcance"
import { useAuth } from "@/lib/auth"
import type { Entity } from "@/lib/entities"
import type { Granularidad } from "@/lib/periodo"

/**
 * Filtros compartidos por todas las vistas financieras.
 *
 * Viven en el layout y no en cada pantalla porque el usuario NO espera que
 * cambiar de P&L a caja le reinicie el año. Es el mismo criterio que
 * `BooksFiltersProvider`: el rango y el libro sobreviven al cambio de pestaña.
 *
 * ===========================================================================
 * EL PAÍS YA NO SE ELIGE AQUÍ: SIGUE A LA SOCIEDAD DE ARRIBA
 * ===========================================================================
 *
 * Cesar: «los filtros en contabilidad, finanzas y dashboard ya deberían de venir
 * aplicados como default a la sociedad que estás viendo (la seleccionada
 * arriba) […] en la parte financiera y contable sí debería de ser por sociedad,
 * y luego si quieres ver suma vas a holding».
 *
 * Antes `pais` arrancaba en `""` —consolidado— y era un desplegable propio. Eso
 * ponía DOS mandos para la misma pregunta, y se contradecían en pantalla: la
 * barra de arriba decía «SIETE AGENCY MÉXICO» y la tabla de abajo enseñaba la
 * suma de los cuatro países. Ganaba el de abajo y no lo decía nadie. En un
 * producto contable ese es el error caro: la cifra es plausible, está bien
 * formateada, y es de otra empresa.
 *
 * Ahora hay UN mando. `pais` se deriva de la sociedad activa y no tiene
 * `setPais`: no es estado que alguien pueda poner, es una consecuencia. Y la
 * suma de las cuatro tiene un sitio —Holding— donde además se hace bien:
 * convirtiendo monedas y eliminando lo que se facturan entre ellas.
 *
 * Ver `lib/alcance.ts` para la regla entera, y para la trampa de que
 * `financial_fact` se lleva por país y no por sociedad.
 */
/**
 * DE QUIÉN SON LAS CIFRAS QUE SE ESTÁN MIRANDO.
 *
 * `sociedad` es Finanzas: una sociedad, siempre, la del selector de arriba.
 * `grupo` es Holding: las cuatro sumadas, y entonces NO hay país que filtrar.
 *
 * Son dos cosas y no un booleano invertido porque hay TRES estados y sólo dos
 * caben en un booleano: «esta sociedad», «el grupo entero» y «todavía no hay
 * sociedad elegida». Confundir el tercero con el segundo es exactamente el
 * defecto que cerró el punto 7 — se leía el consolidado creyendo ver Perú.
 */
export type AlcanceFinanciero = "sociedad" | "grupo"

interface FinanceFilters {
  anio: number
  setAnio: (v: number) => void
  alcance: AlcanceFinanciero
  /**
   * El país con el que se consulta, DERIVADO de la sociedad activa. `null`
   * mientras no haya sociedad elegida, y entonces no se consulta nada: ver
   * `alcanceFinanciero`.
   */
  pais: string | null
  /** La sociedad que manda. Se muestra en la barra como rótulo, no como filtro. */
  sociedad: Entity | null
  /**
   * Otras sociedades del mismo país. Si trae algo, las cifras las incluyen y la
   * barra lo advierte. Ver la nota de `lib/alcance.ts`.
   */
  comparten: Entity[]
  /**
   * ¿Se puede consultar ya?
   *
   * En `grupo`, siempre. En `sociedad`, sólo con una elegida. Las pantallas
   * preguntan ESTO en su `enabled` en vez de `Boolean(pais)`, que en Holding
   * sería falso y dejaría la pantalla vacía para siempre.
   */
  listo: boolean
  /**
   * El valor que viaja al backend como `pais`.
   *
   * `undefined` significa consolidado para `_filtro_pais`, que omite el
   * `AND pais = :pais`. La traducción se hace aquí y en un solo sitio: es lo
   * que evita volver a confundir «sin elegir» con «todo».
   */
  paisQuery: string | undefined
  granularidad: Granularidad
  setGranularidad: (v: Granularidad) => void
}

const Ctx = React.createContext<FinanceFilters | null>(null)

/** Años con datos en el modelo. Fuera de este rango las tablas salen vacías. */
export const ANIOS_DISPONIBLES = [2027, 2026, 2025]

export function FinanceFiltersProvider({
  children,
  alcance = "sociedad",
}: {
  children: React.ReactNode
  /** `grupo` lo pasa Holding. Ver `AlcanceFinanciero`. */
  alcance?: AlcanceFinanciero
}) {
  const { entities, activeEntityId } = useAuth()
  const [anio, setAnio] = React.useState(2026)
  const [granularidad, setGranularidad] = React.useState<Granularidad>("mes")

  const deLaSociedad = React.useMemo(
    () => alcanceFinanciero(entities, activeEntityId),
    [entities, activeEntityId],
  )

  const value = React.useMemo(
    () => ({
      anio,
      setAnio,
      alcance,
      pais: alcance === "grupo" ? null : deLaSociedad.pais,
      sociedad: alcance === "grupo" ? null : deLaSociedad.sociedad,
      comparten: alcance === "grupo" ? [] : deLaSociedad.comparten,
      /*
        SIN SOCIEDAD ELEGIDA SE CONSULTA EL CONSOLIDADO, Y ESO CAMBIÓ.

        Era `deLaSociedad.pais !== null`, o sea: sin sociedad no se consultaba
        nada. La intención era buena —«mejor pedir que elija que devolver el
        consolidado disfrazado de una»— y el resultado en pantalla era este:

          las TARJETAS de arriba llamaban a `/finance/kpis` sin país y mostraban
          1.110.971,72 de ingresos, y la TABLA de abajo, en la misma pantalla,
          decía «No hay estado de resultados para este año y país».

        Dos componentes contestando distinto a la misma pregunta. Y el que
        mentía era el de abajo: no es que no hubiera estado de resultados, es
        que nunca llegó a pedirlo.

        Cesar: «que sí aparezca todo lo que ya tienes y que sólo filtres por
        períodos de tiempo, comparación y sociedades». Sin sociedad elegida el
        alcance es TODAS, y se dice en la barra —no se deja el selector en
        ámbar pidiendo algo que las cifras de arriba ya resolvieron—.

        El riesgo original —leer el consolidado creyendo ver Perú— sigue
        cubierto, y por donde corresponde: cuando SÍ hay sociedad elegida,
        `paisQuery` la respeta. Lo que se arregla es el tercer estado.
      */
      listo: true,
      paisQuery: alcance === "grupo" ? undefined : (deLaSociedad.pais ?? undefined),
      granularidad,
      setGranularidad,
    }),
    [anio, alcance, deLaSociedad, granularidad],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useFinanceFilters(): FinanceFilters {
  const ctx = React.useContext(Ctx)
  if (!ctx) throw new Error("useFinanceFilters fuera de FinanceFiltersProvider")
  return ctx
}
